import { IconShield } from '@tabler/icons-react';

import { Badge, Group, Paper, Text } from '@mantine/core';

import {
    useGetApiNotionPhasePhaseIdRisks,
    useGetApiNotionProjectProjectIdRisks
} from '../../../../api/notion/notion';
import { useDocumentContext } from '../contexts/DocumentContext';

export function RiskRegisterRenderer() {
    const { document } = useDocumentContext();
    const projectId = document.projectId;
    const phaseId = document.notionPhaseId;

    // Use phase risks if document is associated with a phase
    const {
        data: projectRisks,
        isLoading: projectLoading,
        error: projectError
    } = useGetApiNotionProjectProjectIdRisks(projectId, {
        query: { enabled: !!projectId && !phaseId }
    });

    const {
        data: phaseRisks,
        isLoading: phaseLoading,
        error: phaseError
    } = useGetApiNotionPhasePhaseIdRisks(phaseId ?? '', {
        query: { enabled: !!phaseId }
    });

    // Use phase data if available, otherwise fall back to project data
    const risks = phaseId ? phaseRisks : projectRisks;
    const isLoading = phaseId ? phaseLoading : projectLoading;
    const error = phaseId ? phaseError : projectError;

    // Calculate summary data from real risks
    const summaryData =
        risks && risks.length > 0
            ? {
                  count: risks.length.toString(),
                  highRiskCount: risks
                      .filter(risk => {
                          const severity =
                              risk.severity ?? risk.consequences?.name;
                          return severity?.toLowerCase().includes('high');
                      })
                      .length.toString(),
                  mediumRiskCount: risks
                      .filter(risk => {
                          const severity =
                              risk.severity ?? risk.consequences?.name;
                          return severity?.toLowerCase().includes('moderate');
                      })
                      .length.toString(),
                  lowRiskCount: risks
                      .filter(risk => {
                          const severity =
                              risk.severity ?? risk.consequences?.name;
                          return severity?.toLowerCase().includes('low');
                      })
                      .length.toString(),
                  topRisks: risks
                      .slice(0, 3)
                      .map(risk => risk.name ?? `Risk ${risk.riskId}`)
              }
            : {
                  count: risks ? risks.length.toString() : '?',
                  highRiskCount: risks ? '0' : '?',
                  mediumRiskCount: risks ? '0' : '?',
                  lowRiskCount: risks ? '0' : '?',
                  topRisks: isLoading
                      ? ['Loading risks...']
                      : error
                        ? ['Error loading risks']
                        : risks
                          ? ['No risks in project']
                          : ['Loading...']
              };

    return (
        <Paper
            p='md'
            withBorder
            radius='md'
            style={{
                margin: '1rem 0',
                backgroundColor:
                    'light-dark(var(--mantine-color-orange-0), var(--mantine-color-dark-6))'
            }}
        >
            <Group gap='sm' mb='xs'>
                <IconShield size={20} color='var(--mantine-color-orange-6)' />
                <Text fw={600} size='sm'>
                    Risk Register Summary
                </Text>
                <Badge variant='filled' size='sm'>
                    {summaryData.count} risks
                </Badge>
            </Group>

            <Group gap='md' mb='xs'>
                <Group gap='xs'>
                    <Badge color='red' variant='filled' size='xs'>
                        {summaryData.highRiskCount}
                    </Badge>
                    <Text size='xs' c='dimmed'>
                        High
                    </Text>
                </Group>
                <Group gap='xs'>
                    <Badge color='orange' variant='filled' size='xs'>
                        {summaryData.mediumRiskCount}
                    </Badge>
                    <Text size='xs' c='dimmed'>
                        Moderate
                    </Text>
                </Group>
                <Group gap='xs'>
                    <Badge color='yellow' variant='filled' size='xs'>
                        {summaryData.lowRiskCount}
                    </Badge>
                    <Text size='xs' c='dimmed'>
                        Low
                    </Text>
                </Group>
            </Group>

            <div>
                <Text size='xs' c='dimmed' mb={4}>
                    Top Risks:
                </Text>
                {summaryData.topRisks.map((title, index) => (
                    <Text
                        key={index}
                        size='xs'
                        c='dimmed'
                        style={{ marginLeft: '8px' }}
                    >
                        • {title}
                    </Text>
                ))}
            </div>

            <Text size='xs' c='dimmed' mt='xs' fs='italic'>
                Sourced from the linked Notion{' '}
                {document.notionPhaseId ? 'project phase' : 'project'} page.
                Full details will be included in exported documents.
            </Text>
        </Paper>
    );
}
